import { Box, Card, CardContent, Typography } from "@mui/material";
import { AppColors } from "../../constants/AppColors";
import { MERCHANT_INFO } from "../../constants/MerchantInfo";
import { useTranslation } from "../../translations/i18n";

const InformationItem = ({
  label,
  value,
  href,
  wide = false,
}: {
  label: string;
  value: string;
  href?: string;
  wide?: boolean;
}) => {
  return (
    <Box
      sx={{
        minWidth: 0,
        gridColumn: {
          xs: "auto",
          sm: wide ? "1 / -1" : "auto",
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: AppColors.textSecondary,
          fontWeight: 700,
          mb: 0.25,
        }}
      >
        {label}
      </Typography>

      {href ? (
        <Typography
          component="a"
          href={href}
          target={href.startsWith("http") ? "_blank" : undefined}
          rel={href.startsWith("http") ? "noreferrer" : undefined}
          variant="body2"
          sx={{
            color: AppColors.primary,
            fontWeight: 700,
            textDecoration: "none",
            overflowWrap: "anywhere",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {value}
        </Typography>
      ) : (
        <Typography
          variant="body2"
          sx={{
            color: AppColors.textPrimary,
            fontWeight: 700,
            overflowWrap: "anywhere",
          }}
        >
          {value}
        </Typography>
      )}
    </Box>
  );
};

const MerchantInformationCard = () => {
  const { t } = useTranslation();

  return (
    <Card
      sx={{
        mt: 3,
        borderRadius: 3,
        border: `1px solid ${AppColors.border}`,
      }}
    >
      <CardContent
        sx={{
          p: {
            xs: 2,
            md: 2.5,
          },
          "&:last-child": {
            pb: {
              xs: 2,
              md: 2.5,
            },
          },
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 950 }}>
            {t("merchant.title")}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {t("merchant.description")}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "minmax(0, 2fr) minmax(280px, 1fr)",
            },
            gap: {
              xs: 2.5,
              lg: 3,
            },
          }}
        >
          <Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                columnGap: 3,
                rowGap: 1.5,
              }}
            >
              <InformationItem
                label={t("merchant.legalName")}
                value={MERCHANT_INFO.legalName}
                wide
              />

              <InformationItem
                label={t("merchant.address")}
                value={MERCHANT_INFO.address}
                wide
              />

              <InformationItem
                label={t("merchant.businessActivity")}
                value={MERCHANT_INFO.businessActivity}
              />

              <InformationItem
                label={t("merchant.activityCode")}
                value={MERCHANT_INFO.activityCode}
              />

              <InformationItem
                label={t("merchant.registrationNumber")}
                value={MERCHANT_INFO.registrationNumber}
              />

              <InformationItem
                label={t("merchant.taxNumber")}
                value={MERCHANT_INFO.taxNumber}
              />

              <InformationItem
                label={t("merchant.website")}
                value={MERCHANT_INFO.website}
                href={MERCHANT_INFO.website}
              />

              <InformationItem
                label={t("merchant.phone")}
                value={MERCHANT_INFO.phone}
                href={`tel:${MERCHANT_INFO.phone.replace(/\s/g, "")}`}
              />

              <InformationItem
                label={t("merchant.email")}
                value={MERCHANT_INFO.email}
                href={`mailto:${MERCHANT_INFO.email}`}
                wide
              />
            </Box>
          </Box>

          <Box
            sx={{
              borderLeft: {
                xs: "none",
                lg: `1px solid ${AppColors.border}`,
              },
              borderTop: {
                xs: `1px solid ${AppColors.border}`,
                lg: "none",
              },
              pl: {
                xs: 0,
                lg: 3,
              },
              pt: {
                xs: 2,
                lg: 0,
              },
            }}
          >
            <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
              {t("merchant.customerServiceTitle")}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t("merchant.customerServiceDescription")}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gap: 1.5,
              }}
            >
              <InformationItem
                label={t("merchant.address")}
                value={MERCHANT_INFO.customerService.address}
              />

              <InformationItem
                label={t("merchant.phone")}
                value={MERCHANT_INFO.customerService.phone}
                href={`tel:${MERCHANT_INFO.customerService.phone.replace(
                  /\s/g,
                  "",
                )}`}
              />

              <InformationItem
                label={t("merchant.email")}
                value={MERCHANT_INFO.customerService.email}
                href={`mailto:${MERCHANT_INFO.customerService.email}`}
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 2.5,
            pt: 2,
            borderTop: `1px solid ${AppColors.border}`,
          }}
        >
          <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
            {t("merchant.privacyTitle")}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.55 }}
          >
            {t("merchant.privacyDescription")}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MerchantInformationCard;
